import _defineProperty from "@babel/runtime/helpers/defineProperty";
import React from 'react';
import BlockTypes from '../../types/BlockTypes';
import { isAvailable as isLocalStorageAvailable, setItem as setLocalStorageItem, getItem as getLocalStorageItem } from '../../helpers/LocaleStorageHelpers';
import { normalizeNode, shouldNotBeEmpty, shouldBeAtRoot, shouldMergeWithSimilarSiblings, shouldHaveChildrenOfType } from '../utils/NormalizationHelper';
import renderBlocks from '../utils/renderBlocks';
import { BracketsIcon } from '../../components/Icons';
import makeKeyboardBlockShortcut from '../utils/makeKeyboardBlockShortcut';
import makeMarkdownBlockShortcut from '../utils/makeMarkdownBlockShortcut';
import makeToolbarButton, { BASIC_INSERTION, EXPANDED_INSERTION } from '../utils/makeToolbarButton';
import getSyntax from './utils/getSyntax';
import serializers from './serializers';
import CodeContainer from './components/CodeContainer';
import CodeLine from './components/CodeLine';
import onKeyDown from './handlers/onKeyDown';
import onPaste from './handlers/onPaste';
import PrismPlugin from './prism';
import createStore from '../utils/createStore';
import makeBlockActionButton from '../utils/makeBlockActionButton';
import BlockActionTypes from '../../types/BlockActionTypes';
import { TextWrap } from '../../components/Icons';
import './style.css';
var CODE_WRAPPING_LOCAL_STORAGE_KEY = 'slite:editor:codeWrappingEnabled';

function siblingsComparator(a, b) {
  return a.type === b.type && getSyntax(a.nodes.first()) === getSyntax(b.nodes.first());
}

export default (function () {
  var _renderBlocks;

  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$isMobile = _ref.isMobile,
      isMobile = _ref$isMobile === void 0 ? false : _ref$isMobile;

  var store = createStore({
    codeWrappingEnabled: isLocalStorageAvailable() && JSON.parse(getLocalStorageItem(CODE_WRAPPING_LOCAL_STORAGE_KEY))
  });

  var enableCodeWrapping = function enableCodeWrapping() {
    if (isLocalStorageAvailable()) {
      setLocalStorageItem(CODE_WRAPPING_LOCAL_STORAGE_KEY, true);
    }

    store.updateItem('codeWrappingEnabled', true);
  };

  var disableCodeWrapping = function disableCodeWrapping() {
    if (isLocalStorageAvailable()) {
      setLocalStorageItem(CODE_WRAPPING_LOCAL_STORAGE_KEY, false);
    }

    store.updateItem('codeWrappingEnabled', false);
  };

  return [normalizeNode(BlockTypes.CODES, [shouldNotBeEmpty, shouldBeAtRoot, [shouldMergeWithSimilarSiblings, siblingsComparator], [shouldHaveChildrenOfType, BlockTypes.CODE]]), {
    queries: {
      canEnableCodeWrapping: function canEnableCodeWrapping(editor, node) {
        return node.type === BlockTypes.CODES && !store.getItem('codeWrappingEnabled');
      },
      canDisableCodeWrapping: function canDisableCodeWrapping(editor, node) {
        return node.type === BlockTypes.CODES && store.getItem('codeWrappingEnabled');
      }
    },
    commands: {
      enableCodeWrapping: enableCodeWrapping,
      disableCodeWrapping: disableCodeWrapping
    },
    serializers: serializers,
    onKeyDown: onKeyDown,
    onPaste: onPaste
  }, renderBlocks((_renderBlocks = {}, _defineProperty(_renderBlocks, BlockTypes.CODES, function (props) {
    return React.createElement(CodeContainer, Object.assign({}, props, {
      isMobile: isMobile,
      store: store
    }));
  }), _defineProperty(_renderBlocks, BlockTypes.CODE, function (props) {
    return React.createElement(CodeLine, props);
  }), _renderBlocks)), PrismPlugin(), makeKeyboardBlockShortcut('mod+shift+9', BlockTypes.CODE), makeMarkdownBlockShortcut(/^(```)$/, BlockTypes.CODE, {
    triggers: ['space', 'enter']
  }), makeMarkdownBlockShortcut(/^(``)$/, BlockTypes.CODE, {
    triggers: ['`']
  }), makeToolbarButton(BlockTypes.CODE, {
    icon: BracketsIcon,
    title: 'Code snippet',
    visibility: BASIC_INSERTION | EXPANDED_INSERTION,
    canApply: function canApply(editor) {
      return editor.isMutableBlock();
    },
    isActive: function isActive(editor) {
      return editor.isActiveBlock(BlockTypes.CODE);
    },
    onApply: function onApply(editor) {
      return editor.toggleBlock(BlockTypes.CODE);
    }
  }), makeBlockActionButton(BlockActionTypes.ENABLE_CODE_WRAPPING, {
    index: 1,
    type: BlockActionTypes.ACTION,
    doNotHighlightAndFocusOnClose: true,
    icon: TextWrap,
    label: 'Enable Wrapping',
    canApply: function canApply(editor, store) {
      return store.getItem('forcedActions').includes(BlockActionTypes.ENABLE_CODE_WRAPPING) && editor.query('canEnableCodeWrapping', store.getItem('node'));
    },
    onClick: function onClick(editor, node) {
      return editor.command('enableCodeWrapping', node);
    }
  }), makeBlockActionButton(BlockActionTypes.DISABLE_CODE_WRAPPING, {
    index: 1,
    type: BlockActionTypes.ACTION,
    doNotHighlightAndFocusOnClose: true,
    icon: TextWrap,
    label: 'Disable Wrapping',
    canApply: function canApply(editor, store) {
      return store.getItem('forcedActions').includes(BlockActionTypes.DISABLE_CODE_WRAPPING) && editor.query('canDisableCodeWrapping', store.getItem('node'));
    },
    onClick: function onClick(editor, node) {
      return editor.command('disableCodeWrapping', node);
    }
  })];
});